import { useEffect, useRef } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
  addRequestTrack,
  clearForm,
  fetchTrackAndTrackRight,
  hideRequestTrack,
  requestTrackMember,
  setSearchKey,
  setSearchType,
} from 'features/crux/tracker/modal/request/requestTrackSlice'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import { Modal } from 'components/dialog/Modals'
import RadioButton from 'components/form/RadioButton'
import SearchInput from 'components/form/SearchInput'
import Table from 'components/table/Table'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import TrackerServiceTooltip from 'features/crux/tracker/TrackerServiceTooltip'
import Button from 'components/form/Button'
import { success, fail } from 'features/toast/Toast'
import classNames from 'classnames'
import {
  fetchUnlinkedTracks,
  setTab,
  setUnlinkedPage,
} from 'features/crux/tracker/list/trackerListSlice'
import Highlight from 'components/Highlight'
import FixedTable from 'components/table/FixedTable'

const RequestTrack = () => {
  const dispatch = useDispatch()
  const {
    requestTrack: {
      visible,
      searchType,
      searchKey,
      showSearchResult,
      isLoading,
      content,
      totalElements,
      page,
      totalPages,
      isSubmitting,
      requestTracks,
      pagingSearchType,
      pagingSearchKey,
    },
    trackerList: { tab },
  } = useSelector(state => state.tracker)
  const bodyRef = useRef()

  useEffect(() => {
    dispatch(clearForm())
  }, [dispatch, searchType])

  const handleSearch = () => {
    if (searchKey.trim().length > 0) {
      if (bodyRef.current) {
        bodyRef.current.scrollTo({ top: 0 })
      }
      dispatch(
        fetchTrackAndTrackRight({
          searchType,
          searchKey,
          page: 0,
        })
      )
    }
  }

  const handleConfirm = () => {
    if (requestTracks.length > 0) {
      if (tab !== 'unlinked') {
        batch(() => {
          dispatch(setTab('unlinked'))
          dispatch(setUnlinkedPage(0))
        })
      } else {
        dispatch(fetchUnlinkedTracks())
      }
    }
  }

  const handleScroll = ({ target }) => {
    const { offsetHeight, scrollTop, scrollHeight } = target
    const isBottomOfScroll = offsetHeight + scrollTop >= scrollHeight
    if (
      isBottomOfScroll &&
      isLoading === false &&
      page < totalPages - 1 &&
      pagingSearchKey
    ) {
      dispatch(
        fetchTrackAndTrackRight({
          searchType: pagingSearchType,
          searchKey: pagingSearchKey,
          page: page + 1,
        })
      )
    }
  }

  return (
    <Modal className="authority_layer" on={visible}>
      <ConfirmModal
        title="픽셀 & SDK 멤버 권한 요청"
        showCancelBtn={false}
        handleClose={() => dispatch(hideRequestTrack())}
        onConfirm={handleConfirm}>
        <strong className="tit_authority">픽셀 &amp; SDK 찾기</strong>
        <p className="desc_authority">
          픽셀 &amp; SDK 이름 및 ID로 검색하여, 멤버 권한을 요청할 수 있습니다.
        </p>
        <div className="wrap_radioinp">
          <RadioButton.Group onValueChange={v => dispatch(setSearchType(v))}>
            <RadioButton
              checkValue={searchType}
              value="NAME"
              label="픽셀 &amp; SDK 이름"
            />
            <RadioButton
              checkValue={searchType}
              value="ID"
              label="픽셀 &amp; SDK ID"
            />
          </RadioButton.Group>
        </div>
        <SearchInput
          placeholder={`픽셀 & SDK ${
            searchType === 'NAME' ? '이름을' : 'ID를'
          } 입력하세요.`}
          value={searchKey}
          onSearch={handleSearch}
          onChange={({ target: { value } }) => dispatch(setSearchKey(value))}
          type="search"
        />
        {showSearchResult && (
          <>
            <strong className="tit_authority">
              검색 결과 <em className="fc_emph">{totalElements}</em>
            </strong>
            <ul className="list_authority">
              <li>
                검색된 픽셀 &amp; SDK 중 멤버 권한 요청이 가능한 목록입니다.
              </li>
              <li>
                멤버 권한 요청 시, 픽셀 &amp; SDK 마스터에게 이름 및 이메일
                주소가 노출될 수 있습니다.
              </li>
            </ul>
            <FixedTable
              caption="픽셀 &amp; SDK 찾기 결과 표"
              loading={isLoading}
              emptyMessage="검색결과가 없습니다."
              ref={bodyRef}
              onScroll={handleScroll}>
              <Table.Head>
                <Table.Column title="픽셀 &amp; SDK (ID)" width={219} />
                <Table.Column
                  title={
                    <>
                      연동 서비스
                      <HelpTooltip>
                        픽셀 & SDK에 연동된 서비스입니다.
                        <br />
                        마우스오버하면 상세 서비스를 확인할 수 있습니다.
                      </HelpTooltip>
                    </>
                  }
                  width={160}
                />
                <Table.Column title="연동 광고계정" width={133} />
                <Table.Column title="내 권한" />
              </Table.Head>
              <Table.Body>
                {content.map(
                  ({
                    trackId,
                    trackName,
                    role,
                    linkedServiceNamesTitle,
                    tracklinkedAdaccountTitle,
                    trackLinkedServices,
                  }) => (
                    <Table.Row key={trackId} rowId={trackId}>
                      <td>
                        <div className="inner_tbl">
                          {pagingSearchType === 'NAME' ? (
                            <Highlight
                              match={pagingSearchKey}
                              string={trackName}
                            />
                          ) : (
                            trackName
                          )}
                          <p
                            className={classNames({
                              fc_gray:
                                pagingSearchType === 'NAME' ||
                                (pagingSearchType === 'ID' &&
                                  pagingSearchKey !== trackId),
                              fc_emph:
                                pagingSearchType === 'ID' &&
                                pagingSearchKey === trackId,
                            })}>
                            {trackId}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div className="inner_tbl">
                          {linkedServiceNamesTitle ? (
                            <TrackerServiceTooltip
                              position="top center"
                              data={trackLinkedServices}>
                              {linkedServiceNamesTitle}
                            </TrackerServiceTooltip>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="inner_tbl">
                          {tracklinkedAdaccountTitle || '-'}
                        </div>
                      </td>
                      {role === 'REQUEST' ? (
                        <td>
                          <div className="inner_tbl">멤버 권한 요청 중</div>
                        </td>
                      ) : (
                        <td className="align_c">
                          <div className="inner_tbl">
                            <Button
                              size="small"
                              disabled={isSubmitting}
                              onClick={async () => {
                                const { error, payload } = await dispatch(
                                  requestTrackMember(trackId)
                                )
                                if (error) {
                                  fail(
                                    payload?.message ||
                                      '알 수 없는 에러가 발생했습니다.'
                                  )
                                } else {
                                  success('멤버 요청이 완료되었습니다.')
                                  dispatch(addRequestTrack(trackId))
                                }
                              }}>
                              멤버 권한 요청
                            </Button>
                          </div>
                        </td>
                      )}
                    </Table.Row>
                  )
                )}
              </Table.Body>
            </FixedTable>
          </>
        )}
      </ConfirmModal>
    </Modal>
  )
}
export default RequestTrack
